import type { ReactNode } from 'react';

import type { DateRangeOption } from '@shared/features/views/components/entities-filters/type';
import type { InfiniteListProps } from '@design-system/molecules/infinite-list';
import type { ValueOf } from '@shared/types/utilities';
import type { STATIC_FILTER_ENTITY_TYPES } from '@shared/features/views/components/entities-filters/config';
import type { TView } from '@shared/types/view';

export const FILTER_KIND = {
  simple: 'simple',
  entity: 'entity',
  dateRange: 'date_range',
  phone: 'phone',
} as const;

import type { TFilterColor } from '../configs/filter-colors';

// ------------------------------ Shared Column Filter Config ------------------------------

export type TSharedColumnFilterConfig = {
  color?: TFilterColor | (string & {});
  icon?: ReactNode;
  queryKey?: string;
  facetName: string;
  /**
   * Leads table filters include a “None” choice by default.
   * Set `false` to omit it (for example the duplicates filter).
   */
  withNoneOption?: boolean;
};

// ------------------------------ Simple Filter Config ------------------------------

export type TSimpleFilterConfig = TSharedColumnFilterConfig & {
  kind: typeof FILTER_KIND.simple;
  /**
   * Option values are API fields sent as booleans.
   * A bare value sends `true`. A value prefixed with `exclude:` sends `false`.
   */
  booleanValues?: boolean;
  options: {
    value: string;
    label: string;
    renderLabel?: ReactNode;
  }[];
  /** Opt in to labelling the active-filter chip: the facet is shown under
   * this name, and each value by its option's label instead of its raw
   * value (e.g. "Active" for `true`). */
  friendlyField?: string;
};

// ------------------------------ Entity Filter Config ------------------------------

export type TRenderOptionItemCtx<T> = {
  item: T;
  selected: boolean;
  onToggle: () => void;
};

export type TRenderSelectedValuesCtx = {
  filter: TView['static_filters'][number];
};

export type TEntityFilterConfig<
  T extends Record<string, unknown> = Record<string, unknown>,
> = Omit<InfiniteListProps<T>, 'renderItem'> &
  TSharedColumnFilterConfig & {
    kind: typeof FILTER_KIND.entity;
    getValue: (item: T) => string;
    renderItem: (ctx: TRenderOptionItemCtx<T>) => ReactNode;
    entityType: ValueOf<typeof STATIC_FILTER_ENTITY_TYPES>;
    renderChip?: (ctx: { value: string }) => ReactNode;
  };

// ------------------------------ Date Range Filter Config ------------------------------

export type TDateRangeFilterConfig = TSharedColumnFilterConfig & {
  kind: typeof FILTER_KIND.dateRange;
  entityType: (typeof STATIC_FILTER_ENTITY_TYPES)['date'];
  includedDateRanges?: DateRangeOption[];
  panelMode?: 'preset' | 'direct';
};

// ------------------------------ Phone Filter Config ------------------------------

export type TPhoneFilterConfig = TSharedColumnFilterConfig & {
  kind: typeof FILTER_KIND.phone;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TFilterConfig<T extends Record<string, unknown> = any> =
  | TSimpleFilterConfig
  | TEntityFilterConfig<T>
  | TDateRangeFilterConfig
  | TPhoneFilterConfig;
